import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { kitFlavorOptions, kitSizeOptions } from "../utils/bundlePricing";
import { priceForSize } from "../utils/variant";
import { api } from "../lib/api";
import { trackAddToCart } from "../components/leovera/Analytics";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "leovera_cart_v103";
const PROVINCE_KEY = "leovera_province_v103";
const COMUNE_KEY = "leovera_comune_v1";
const DISCOUNT_KEY = "leovera_discount_v1";

// Mirrors backend discounts.discount_allows/discounts_compatible exactly — a UX-layer
// mirror for instant client feedback, the server re-checks authoritatively at checkout.
const discountAllows = (doc, otherCode) => {
  const mode = doc?.stack_mode || "exclusive";
  if (mode === "all") return true;
  if (mode === "specific") return (doc?.compatible_codes || []).includes(otherCode);
  return false; // "exclusive" allows nobody, not even another exclusive code
};
const discountsCompatible = (a, b) => discountAllows(a, b?.code) && discountAllows(b, a?.code);

export const CartProvider = ({ children }) => {
  const { getToken, user } = useAuth();
  // Who is validating is resolved SERVER-SIDE from this header (read fresh at
  // call-time, so it's correct even immediately after a just-completed login —
  // unlike `user` from this closure, which still holds the previous render's
  // value right after `await login()` resolves). Never send identity in the
  // request body — the backend ignores it by design (discounts.py).
  const authHeaders = useCallback(() => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [getToken]);
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [province, setProvinceState] = useState(() => {
    try { return localStorage.getItem(PROVINCE_KEY) || ""; } catch { return ""; }
  });
  // Confirms the customer is inside the comune (city) of Siracusa/Catania —
  // required for free shipping on zones restricted by postal code ('caps').
  const [comuneConfirmed, setComuneConfirmed] = useState(() => {
    try { return localStorage.getItem(COMUNE_KEY) === "1"; } catch { return false; }
  });
  const setProvince = useCallback((p) => {
    setProvinceState(p);
    setComuneConfirmed(false); // reset confirmation whenever the province changes
  }, []);
  const [discountCodes, setDiscountCodes] = useState(() => {
    try {
      const raw = localStorage.getItem(DISCOUNT_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [discounts, setDiscounts] = useState([]); // one validate() result per code, in order
  const [discountLoading, setDiscountLoading] = useState(false);
  // Last REJECTED attempt only (never a currently-applied code) — kept separate from
  // `discounts` (which must always mirror `discountCodes` exactly, for the amount/
  // exclusivity math) so the UI can show an inline error + "Accedi" CTA without ever
  // wiping out an already-applied code's valid result.
  const [discountError, setDiscountError] = useState(null);
  // Not-yet-applied preview of the automatic welcome discount (eligibility/amount
  // require is_new_customer, which in turn needs email/phone/name — unknown until
  // the checkout form is filled in). Populated via refreshWelcomePreview(), consumed
  // ONLY to keep typed-code combinability checks accurate (see runValidateAll) —
  // it never becomes a chip of its own and never adds its own amount to the preview
  // total (that has always only ever shown up after the order is actually placed).
  const [welcomePreview, setWelcomePreview] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  useEffect(() => {
    try { localStorage.setItem(PROVINCE_KEY, province); } catch { /* ignore */ }
  }, [province]);

  useEffect(() => {
    try { localStorage.setItem(COMUNE_KEY, comuneConfirmed ? "1" : "0"); } catch { /* ignore */ }
  }, [comuneConfirmed]);

  useEffect(() => {
    try { localStorage.setItem(DISCOUNT_KEY, JSON.stringify(discountCodes)); } catch { /* ignore */ }
  }, [discountCodes]);

  const addItem = useCallback((product, qty = 1, opts = {}) => {
    const flavor = opts.flavor || "";
    const size = opts.size || "";
    const variantKey = `${product.id}__${flavor}__${size}`;
    setItems((arr) => {
      const idx = arr.findIndex((i) => i.variant_key === variantKey);
      if (idx !== -1) {
        const next = [...arr];
        next[idx] = { ...next[idx], quantity: Math.max(1, next[idx].quantity + qty) };
        return next;
      }
      return [...arr, {
        variant_key: variantKey,
        product_id: product.id,
        name: product.name,
        brand: product.brand,
        price: priceForSize(product, size),
        image: product.image,
        tier: product.tier,
        stock: product.stock,
        flavor,
        size,
        quantity: Math.max(1, qty),
      }];
    });
    toast.success("✓ Prodotto aggiunto al carrello", {
      description: product.name,
      duration: 3000,
    });
    trackAddToCart(product, qty, priceForSize(product, size));
    setOpen(true);
  }, []);

  // Kit/bundle add: the kit's price is ALWAYS anchored to the admin's
  // default_size (fixed-price kit policy) — whichever gusto/formato the
  // customer picks among the allowed, in-stock options only changes what
  // ships, never the total. Checkout re-verifies this server-side.
  const addBundle = useCallback((bundle, productsById, flavorChoices = {}, sizeChoices = {}) => {
    const ratio = bundle.discount_type === "percentage"
      ? Math.max(0, 1 - (Number(bundle.discount_value) || 0) / 100)
      : (() => {
          const defaultTotal = (bundle.items || []).reduce((acc, it) => {
            const p = productsById[it.product_id];
            return p ? acc + priceForSize(p, it.default_size) * (it.quantity || 1) : acc;
          }, 0);
          return defaultTotal > 0 ? Math.max(0, Math.min(1, (Number(bundle.discount_value) || 0) / defaultTotal)) : 1;
        })();
    setItems((arr) => {
      let next = [...arr];
      (bundle.items || []).forEach((it) => {
        const product = productsById[it.product_id];
        if (!product) return;
        const sizeOptions = kitSizeOptions(it, product, flavorChoices[it.product_id]);
        const pickedSize = sizeChoices[it.product_id];
        const size = sizeOptions.includes(pickedSize) ? pickedSize : (sizeOptions[0] ?? it.default_size ?? "");
        const flavorOptions = kitFlavorOptions(it, product, size);
        const picked = flavorChoices[it.product_id];
        const flavor = flavorOptions.includes(picked) ? picked : (flavorOptions[0] ?? it.default_flavor ?? "");
        const qty = it.quantity || 1;
        // Pricing basis is always the admin's default_size, never the chosen one.
        const originalPrice = priceForSize(product, it.default_size ?? "");
        const discountedPrice = Math.round(originalPrice * ratio * 100) / 100;
        const variantKey = `${product.id}__${flavor}__${size}__bundle_${bundle.id}`;
        const idx = next.findIndex((i) => i.variant_key === variantKey);
        if (idx !== -1) {
          next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        } else {
          next.push({
            variant_key: variantKey,
            product_id: product.id,
            name: product.name,
            brand: product.brand,
            price: discountedPrice,
            original_price: originalPrice,
            image: product.image,
            tier: product.tier,
            stock: product.stock,
            flavor, size,
            quantity: qty,
            bundle_id: bundle.id,
            bundle_name: bundle.name,
          });
        }
      });
      // "Prezzo fisso": the cart must show exactly the admin's kit price, so the
      // rounding residual of the per-line split goes on one single-unit line
      // (mirrors the server-side correction in _price_cart_lines).
      if (bundle.discount_type === "fixed_price") {
        const kitLines = next.filter((i) => i.bundle_id === bundle.id);
        const target = Math.round((Number(bundle.discount_value) || 0) * 100) / 100;
        const current = Math.round(kitLines.reduce((a, i) => a + i.price * i.quantity, 0) * 100) / 100;
        const residual = Math.round((target - current) * 100) / 100;
        const carrier = kitLines.find((i) => i.quantity === 1);
        if (residual && carrier && carrier.price + residual > 0) {
          const at = next.indexOf(carrier);
          next[at] = { ...carrier, price: Math.round((carrier.price + residual) * 100) / 100 };
        }
      }
      return next;
    });
    toast.success(`✓ ${bundle.name} aggiunto al carrello`, { duration: 3000 });
    setOpen(true);
  }, []);

  const removeItem = useCallback((variantKeyOrProductId) => {
    setItems((arr) => arr.filter((i) => i.variant_key !== variantKeyOrProductId && i.product_id !== variantKeyOrProductId));
  }, []);

  const setQuantity = useCallback((variantKeyOrProductId, qty) => {
    setItems((arr) => arr.map((i) => (i.variant_key === variantKeyOrProductId || i.product_id === variantKeyOrProductId)
      ? { ...i, quantity: Math.max(1, Math.min(qty, i.stock || qty)) }
      : i));
  }, []);

  const clear = useCallback(() => { setItems([]); setDiscountCodes([]); setDiscounts([]); setWelcomePreview(null); }, []);

  const subtotal = useMemo(
    () => items.reduce((acc, i) => acc + i.price * i.quantity, 0),
    [items]
  );
  const itemCount = useMemo(
    () => items.reduce((acc, i) => acc + i.quantity, 0),
    [items]
  );

  // Lets the checkout page mirror the server's real welcome-discount combinability
  // rules (stack_mode/compatible_codes, is_new_customer) once it knows who's asking —
  // called from CheckoutPage whenever the email/phone/name fields settle.
  const clearWelcomePreview = useCallback(() => setWelcomePreview(null), []);
  const refreshWelcomePreview = useCallback(async ({ email, phone, name } = {}) => {
    if (!items.length) { setWelcomePreview(null); return; }
    try {
      const { data } = await api.post("/discounts/welcome-preview", {
        gross_total: Math.round(subtotal * 100) / 100,
        customer_email: email || "", customer_phone: phone || "", customer_name: name || "",
      }, { headers: authHeaders() });
      setWelcomePreview(data?.valid ? data : null);
    } catch {
      setWelcomePreview(null);
    }
  }, [items.length, subtotal, authHeaders]);

  // Validates ALL applied codes, one after another, against the REMAINING amount left
  // after the previous ones — mirrors the server's sequential stacking exactly so the
  // preview total always matches what checkout will actually charge. Also mirrors the
  // server's compatibility rule: if any valid result turns out to be "exclusive" (e.g. an
  // admin flipped a code's mode, or a stale combo was restored from localStorage), only the
  // single best exclusive code survives; otherwise only the largest mutually-compatible
  // subset of the valid codes is kept — everything else is pruned from BOTH the results and
  // the applied-codes list, so the preview never shows a bigger discount than checkout will
  // actually charge. A code that is itself invalid (wrong, expired, or availability-gated
  // for the current guest/registered identity) is ALSO pruned here — this self-heals a code
  // that was valid when applied but becomes invalid later (login/logout, cart change). A
  // transient failure (network/429/5xx) is NOT treated as "invalid": it is left in place and
  // simply retried on the next pass, so a blip never permanently drops a real code.
  const validationSeqRef = useRef(0);
  const runValidateAll = useCallback(async (codes, curItems, curSubtotal) => {
    const seq = ++validationSeqRef.current;
    if (!codes.length || curItems.length === 0) { setDiscounts([]); setDiscountLoading(false); return { results: [], finalCodes: [], dropped: [], suppressed: [] }; }
    setDiscountLoading(true);
    try {
      const results = [];
      let remaining = curSubtotal;
      for (const code of codes) {
        try {
          const { data } = await api.post("/discounts/validate", {
            code,
            subtotal: Math.round(remaining * 100) / 100,
            items: curItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.size || "", unit_price: i.price })),
          }, { headers: authHeaders() });
          results.push(data);
          if (data?.valid) {
            const amt = Math.min(Number(data.value_eur) || 0, remaining);
            remaining = Math.max(0, Math.round((remaining - amt) * 100) / 100);
          }
        } catch {
          // A network/429/5xx failure is NOT a definitive "this code is invalid" answer —
          // mark it transient so it keeps its place in the applied list and is simply
          // retried on the next pass, instead of permanently vanishing on a blip.
          results.push({ valid: false, transient: true, message: "Errore nella validazione del codice, riprova" });
        }
      }
      if (seq !== validationSeqRef.current) {
        // A newer call (e.g. a login firing right after a cart edit) already resolved —
        // applying this stale result would overwrite it, so bail out without touching state.
        return { results, finalCodes: codes, dropped: [], suppressed: [] };
      }
      const indexed = codes.map((code, i) => ({ code, d: results[i] }));
      // A code that is definitively invalid (wrong, expired, or availability-gated for the
      // current guest/registered identity) never belongs in the applied list — this also
      // self-heals a code that WAS valid when applied but becomes invalid later (e.g. the
      // customer logs out/in, or the cart composition changes).
      const dropCodes = new Set(indexed.filter((e) => !e.d?.valid && !e.d?.transient).map((e) => e.code));
      const validEntries = indexed.filter((e) => e.d?.valid);
      // Pass A — typed-code-vs-typed-code only (pre-existing rule, unrelated to welcome):
      // a conflict here is a genuine, permanent drop (code removed from discountCodes),
      // exactly as before this feature.
      const exclusivesA = validEntries.filter((e) => e.d.stack_mode === "exclusive");
      let survivorsA;
      if (exclusivesA.length) {
        const best = exclusivesA.reduce((a, b) => (Number(b.d.value_eur) || 0) > (Number(a.d.value_eur) || 0) ? b : a);
        survivorsA = validEntries.filter((e) => e.code === best.code);
        for (const e of validEntries) if (e.code !== best.code) dropCodes.add(e.code);
      } else {
        const chosen = [];
        for (const e of validEntries) {
          if (chosen.every((c) => discountsCompatible(e.d, c.d))) chosen.push(e);
          else dropCodes.add(e.code);
        }
        survivorsA = chosen;
      }
      // Pass B — survivors of A vs. the not-yet-applied welcome preview. The customer typed
      // these codes on purpose, so a conflict here never deletes them: it only SUPPRESSES
      // them from the total/preview while welcome wins. Since this whole function re-runs
      // from the persisted `discountCodes` + the latest `welcomePreview` every time, the
      // suppression lifts itself the moment welcome stops being eligible (returning
      // customer, invalid email, disabled/expired) — no "bring it back" logic needed.
      const welcomeEntry = welcomePreview?.valid ? { code: "__WELCOME__", d: welcomePreview } : null;
      const suppressedByWelcome = new Set();
      if (welcomeEntry) {
        const poolB = [...survivorsA, welcomeEntry];
        const exclusivesB = poolB.filter((e) => e.d.stack_mode === "exclusive");
        if (exclusivesB.length) {
          const best = exclusivesB.reduce((a, b) => (Number(b.d.value_eur) || 0) > (Number(a.d.value_eur) || 0) ? b : a);
          if (best.code === "__WELCOME__") {
            for (const e of poolB) if (e.code !== "__WELCOME__") suppressedByWelcome.add(e.code);
          }
          // else: the typed exclusive code wins outright — welcome simply won't be counted
          // (handled separately by the welcomeAppliedAmount derivation), nothing to suppress.
        } else {
          const chosenB = [];
          for (const e of poolB) {
            if (chosenB.every((c) => discountsCompatible(e.d, c.d))) chosenB.push(e);
            else if (e.code !== "__WELCOME__") suppressedByWelcome.add(e.code);
          }
        }
      }
      const finalIndexed = indexed.filter((e) => !dropCodes.has(e.code));
      const finalCodes = finalIndexed.map((e) => e.code);
      const finalResults = finalIndexed.map((e) => (suppressedByWelcome.has(e.code)
        ? { ...e.d, valid: false, suppressed_by_welcome: true, message: "In pausa: sostituito temporaneamente dallo sconto di benvenuto automatico" }
        : e.d));
      setDiscounts(finalResults);
      if (finalCodes.length !== codes.length || finalCodes.some((c, i) => c !== codes[i])) {
        setDiscountCodes(finalCodes);
      }
      // Toasting is left to the caller: a user-initiated add already shows its own
      // success/failure toast, while the background revalidation effect toasts once
      // per silently-dropped code using this list (stable `id` so re-renders don't repeat it).
      const dropped = indexed
        .filter((e) => dropCodes.has(e.code))
        .map((e) => (e.d?.valid
          ? { ...e, d: { ...e.d, message: e.d.non_stackable_message || "Questo codice non è più cumulabile con un altro sconto applicato." } }
          : e))
        .filter((e) => e.d?.message);
      const suppressed = indexed
        .filter((e) => suppressedByWelcome.has(e.code))
        .map((e) => ({ ...e, d: { ...e.d, message: "In pausa: sostituito temporaneamente dallo sconto di benvenuto automatico" } }));
      return { results, finalCodes, dropped, suppressed };
    } finally { if (seq === validationSeqRef.current) setDiscountLoading(false); }
  }, [authHeaders, welcomePreview]);

  // Enforces the compatibility rule BEFORE touching the applied-codes list: two codes may
  // combine only if EACH one's own stack_mode/compatible_codes allows the other back. Mirrors
  // the authoritative server-side enforcement in `_apply_discount_and_shipping` — this is
  // just the UX layer so the customer gets instant feedback (block / replace) without a
  // round-trip to checkout.
  const addDiscountCode = useCallback(async (code) => {
    const normalized = (code || "").trim().toUpperCase();
    if (!normalized) return null;
    setDiscountError(null);
    if (discountCodes.includes(normalized)) {
      toast.error("Codice già applicato");
      return null;
    }

    // Probe the new code alone (against the full subtotal) just to learn its
    // stack_mode/compatible_codes + custom message before deciding how it combines
    // with whatever is already applied.
    let probe;
    try {
      const { data } = await api.post("/discounts/validate", {
        code: normalized,
        subtotal: Math.round(subtotal * 100) / 100,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.size || "", unit_price: i.price })),
      }, { headers: authHeaders() });
      probe = data;
    } catch {
      probe = { valid: false, message: "Errore nella validazione del codice" };
    }

    if (!probe?.valid) {
      toast.error(probe?.message || "Codice non valido");
      setDiscountError(probe);
      return probe;
    }

    // Find the first already-applied code (if any) that does NOT mutually allow combining
    // with the new one — either side refusing is enough to block the pair.
    const activeConflict = discounts.find((d) => d?.valid && !discountsCompatible(probe, d));
    // An exclusive code replaces whatever (non-exclusive) codes are already applied — but
    // if an EXCLUSIVE code is already active, it blocks ANY new addition (even another
    // exclusive one), it never gets swapped out silently.
    const existingExclusive = discounts.find((d) => d?.valid && d.stack_mode === "exclusive");
    const isReplace = probe.stack_mode === "exclusive" && discountCodes.length > 0 && !existingExclusive;
    if (activeConflict && !isReplace) {
      // Prefer whichever side is ACTUALLY refusing the pair, so the customer sees the
      // right code's own explanation rather than a generic fallback: an already-active
      // exclusive code always explains itself first (it blocks everything by definition);
      // otherwise show whichever of the two specifically declines the other.
      let msg;
      if (activeConflict.stack_mode === "exclusive") {
        msg = activeConflict.non_stackable_message;
      } else if (!discountAllows(probe, activeConflict.code)) {
        msg = probe.non_stackable_message;
      } else {
        msg = activeConflict.non_stackable_message;
      }
      msg = msg || "Questo codice non è cumulabile con uno dei codici già applicati.";
      toast.error(msg);
      setDiscountError({ valid: false, message: msg });
      return { valid: false, message: msg };
    }

    // An exclusive code replaces whatever codes are already applied; otherwise the new
    // (now confirmed fully compatible) code simply joins the list.
    const nextCodes = isReplace ? [normalized] : [...discountCodes, normalized];

    const { results, finalCodes, dropped, suppressed } = await runValidateAll(nextCodes, items, subtotal);
    const res = results[results.length - 1];
    const toastDrop = (e) => toast.error(e.d.message, { id: `discount-drop-${e.code}`, description: e.code });
    const toastSuppress = (e) => toast.info(e.d.message, { id: `discount-suppress-${e.code}`, description: e.code });
    if (res?.valid) {
      setDiscountCodes(finalCodes);
      if (suppressed.some((e) => e.code === normalized)) {
        // The code is fine and stays applied, but the automatic welcome discount wins
        // right now — say so instead of claiming it's active. Same `id` as the
        // revalidation effect's own toast so sonner merges them into one.
        toast.info("Codice salvato, in pausa: lo sconto di benvenuto è già più vantaggioso", { id: `discount-suppress-${normalized}`, description: normalized });
      } else {
        toast.success(isReplace ? "✓ Codice applicato al posto del precedente (non cumulabile)" : "✓ Codice sconto applicato", { description: normalized });
      }
      // The new code is fine, but it may have silently bumped an already-applied one
      // out of the combo (rare — compatibility was pre-screened above) — say so.
      dropped.forEach(toastDrop);
      suppressed.filter((e) => e.code !== normalized).forEach(toastSuppress);
    } else {
      toast.error(res?.message || "Codice non valido");
      setDiscountError(res);
      // Anything else that got dropped in this same pass (besides the new code itself,
      // whose rejection is already shown above) still deserves its own toast.
      dropped.filter((e) => e.code !== normalized).forEach(toastDrop);
      // runValidateAll above validated `nextCodes` (one entry longer than what's
      // actually still applied) — resync `discounts` with the unchanged `discountCodes`
      // so a failed attempt doesn't leave a stale error/result lingering on screen.
      if (discountCodes.length) {
        await runValidateAll(discountCodes, items, subtotal);
      } else {
        setDiscounts([]);
      }
    }
    return res;
  }, [discountCodes, discounts, items, subtotal, authHeaders, runValidateAll]);

  const removeDiscountCode = useCallback((code) => {
    setDiscountCodes((cur) => cur.filter((c) => c !== code));
    setDiscountError(null);
  }, []);

  // Re-validate whenever the cart total/variants change, OR the logged-in identity
  // changes (login/logout) — an availability-gated code (registered/guest-only) can
  // flip valid/invalid purely because of who is now asking, so it must be re-checked
  // immediately rather than waiting for the next cart edit.
  const cartSignature = useMemo(
    () => items.map((i) => `${i.product_id}:${i.size}:${i.quantity}`).join("|"),
    [items]
  );
  useEffect(() => {
    if (!discountCodes.length || items.length === 0) {
      // Invalidate any in-flight runValidateAll pass — otherwise it can resolve AFTER
      // this reset and silently re-apply a discount for a code that was just removed
      // or a cart that was just emptied, so the preview no longer matches what checkout
      // would actually charge.
      validationSeqRef.current += 1;
      setDiscounts([]);
      setDiscountLoading(false);
      return;
    }
    runValidateAll(discountCodes, items, subtotal).then(({ dropped, suppressed }) => {
      for (const e of dropped) {
        toast.error(e.d.message, { id: `discount-drop-${e.code}`, description: e.code });
      }
      // A softer, dismissable-by-dedupe info toast — the code is still applied, just not
      // currently contributing because the automatic welcome discount wins right now.
      for (const e of suppressed) {
        toast.info(e.d.message, { id: `discount-suppress-${e.code}`, description: e.code });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, discountCodes, cartSignature, user?.id, welcomePreview]);

  const discountAmount = useMemo(() => {
    let remaining = subtotal;
    let total = 0;
    for (const d of discounts) {
      if (d?.valid) {
        const amt = Math.min(Number(d.value_eur) || 0, remaining);
        total += amt;
        remaining = Math.max(0, remaining - amt);
      }
    }
    return Math.round(total * 100) / 100;
  }, [discounts, subtotal]);

  // Not a chip/typed code, but if the welcome discount survives the SAME compatibility
  // pool as the currently-applied typed codes (see runValidateAll), it WILL reduce what
  // checkout actually charges — so the preview total must reflect it too, otherwise an
  // "esclusivo"/"specific" welcome config can make the preview show MORE than the real
  // charge (see CheckoutPage, which adds this to its displayed discount amount).
  const welcomeAppliedAmount = useMemo(() => {
    if (!welcomePreview?.valid) return 0;
    const validEntries = discounts.filter((d) => d?.valid).map((d) => ({ code: d.code, d }));
    const welcomeEntry = { code: "__WELCOME__", d: welcomePreview };
    const pool = [...validEntries, welcomeEntry];
    const exclusives = pool.filter((e) => e.d.stack_mode === "exclusive");
    let survives;
    if (exclusives.length) {
      const best = exclusives.reduce((a, b) => (Number(b.d.value_eur) || 0) > (Number(a.d.value_eur) || 0) ? b : a);
      survives = best.code === "__WELCOME__";
    } else {
      const chosen = [];
      for (const e of pool) {
        if (chosen.every((c) => discountsCompatible(e.d, c.d))) chosen.push(e);
      }
      survives = chosen.some((e) => e.code === "__WELCOME__");
    }
    if (!survives) return 0;
    // Capped against the room left after the typed codes already in discountAmount — a
    // close approximation of the server's sequential maths (welcome applies to the full
    // subtotal FIRST, typed codes to what's left after). A percentage-type typed code
    // combined with welcome can be a few cents smaller server-side; fixed-amount combos
    // match exactly.
    return Math.min(Number(welcomePreview.value_eur) || 0, Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100));
  }, [discounts, welcomePreview, subtotal, discountAmount]);
  const discountValid = discounts.some((d) => d?.valid);
  const discountFreeShipping = discounts.some((d) => d?.valid && d.free_shipping);

  const value = {
    items, itemCount, subtotal,
    discountCodes, discounts, discountError, discountValid, discountAmount, discountFreeShipping, discountLoading,
    addDiscountCode, removeDiscountCode,
    welcomePreview, welcomeAppliedAmount, refreshWelcomePreview, clearWelcomePreview,
    open, setOpen,
    province, setProvince,
    comuneConfirmed, setComuneConfirmed,
    addItem, removeItem, setQuantity, clear, addBundle,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
